import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import CustomPageHeader from "../components/pages/CustomPageHeader";
import { createPoll } from "../api/polls";

const zero = (n) => String(n).padStart(2, "0");

// --------------------------------------
// CalendarPopover - month view calendar
// --------------------------------------
function CalendarPopover({ initialDate, onCancel, onConfirm }) {
  const today = initialDate || new Date();
  const [viewDate, setViewDate] = useState(today);
  const [selDate, setSelDate] = useState(today);

  const startOfMonth = new Date(viewDate.getFullYear(), viewDate.getMonth(), 1);
  const endOfMonth = new Date(
    viewDate.getFullYear(),
    viewDate.getMonth() + 1,
    0
  );
  const firstWeekday = startOfMonth.getDay();
  const daysInMonth = endOfMonth.getDate();

  const days = [];
  const prevMonthEnd = new Date(
    viewDate.getFullYear(),
    viewDate.getMonth(),
    0
  ).getDate();

  for (let i = firstWeekday - 1; i >= 0; i--) {
    days.push({
      day: prevMonthEnd - i,
      inMonth: false,
      date: new Date(
        viewDate.getFullYear(),
        viewDate.getMonth() - 1,
        prevMonthEnd - i
      ),
    });
  }
  for (let d = 1; d <= daysInMonth; d++) {
    days.push({
      day: d,
      inMonth: true,
      date: new Date(viewDate.getFullYear(), viewDate.getMonth(), d),
    });
  }
  while (days.length % 7 !== 0) {
    const nextDay = days.length - (firstWeekday + daysInMonth) + 1;
    days.push({
      day: nextDay,
      inMonth: false,
      date: new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, nextDay),
    });
  }

  const { t } = useTranslation();
  const weekdays = [
    t("calendar_su"),
    t("calendar_mo"),
    t("calendar_tu"),
    t("calendar_we"),
    t("calendar_th"),
    t("calendar_fr"),
    t("calendar_sa"),
  ];
  const monthNames = [
    t("calendar_january"),
    t("calendar_february"),
    t("calendar_march"),
    t("calendar_april"),
    t("calendar_may"),
    t("calendar_june"),
    t("calendar_july"),
    t("calendar_august"),
    t("calendar_september"),
    t("calendar_october"),
    t("calendar_november"),
    t("calendar_december"),
  ];

  return (
    <div className="bg-white dark:bg-[#0a0a0a] rounded-2xl shadow-2xl w-full max-w-sm sm:w-80 z-50 overflow-hidden border border-gray-200 dark:border-[#2b2b2b]">
      {/* Header */}
      <div className="bg-[#6d29f6] text-white p-6">
        <div className="flex items-center justify-between mb-4">
          <button
            type="button"
            aria-label={t("calendar_prev_month")}
            onClick={() =>
              setViewDate(
                new Date(viewDate.getFullYear(), viewDate.getMonth() - 1, 1)
              )
            }
          >
            ◀
          </button>
          <div className="text-center">
            <div className="text-lg font-semibold">
              {monthNames[viewDate.getMonth()]}
            </div>
            <div className="text-sm opacity-90">{viewDate.getFullYear()}</div>
          </div>
          <button
            type="button"
            aria-label={t("calendar_next_month")}
            onClick={() =>
              setViewDate(
                new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 1)
              )
            }
          >
            ▶
          </button>
        </div>
        <div className="text-center text-lg font-semibold">
          {selDate.toDateString()}
        </div>
      </div>

      {/* Body */}
      <div className="p-4">
        <div className="grid grid-cols-7 text-center text-xs font-medium text-gray-600 dark:text-gray-400 mb-3">
          {weekdays.map((w, i) => (
            <div key={i}>{w}</div>
          ))}
        </div>

        <div className="grid grid-cols-7 gap-1">
          {days.map((cell, idx) => {
            const isSelected =
              selDate.toDateString() === cell.date.toDateString();
            return (
              <button
                type="button"
                key={idx}
                onClick={() => setSelDate(cell.date)}
                className={`w-10 h-10 rounded-full transition-all duration-150 ${
                  isSelected
                    ? "bg-[#6d29f6] text-white"
                    : cell.inMonth
                    ? "text-gray-900 dark:text-gray-100 hover:bg-[#f1f1f1] dark:hover:bg-[#2f3050]"
                    : "text-gray-400 dark:text-gray-600"
                }`}
              >
                {cell.day}
              </button>
            );
          })}
        </div>
      </div>

      {/* Footer */}
      <div className="flex justify-between px-6 py-4 border-t border-gray-200 dark:border-[#2e2e2e] bg-gray-50 dark:bg-[#252739]">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 rounded-lg font-medium text-gray-700 dark:text-[#ff6b6b] bg-gray-200 dark:bg-[#1f1f1f] hover:bg-gray-300 dark:hover:bg-[#6d29f6] dark:hover:text-white transition"
        >
          {t("cancel")}
        </button>
        <button
          type="button"
          onClick={() => onConfirm(selDate)}
          className="px-4 py-2 rounded-lg font-medium text-white bg-[#6d29f6] hover:bg-[#5b22d1] transition"
        >
          {t("ok")}
        </button>
      </div>
    </div>
  );
}

// --------------------------------------
// ClockPopover - circular clock interface
// --------------------------------------

function ClockPopover({ initialTime, onCancel, onConfirm }) {
  const initHours = initialTime?.hours ?? 12;
  const initMinutes = initialTime?.minutes ?? 0;

  const [hours, setHours] = useState(initHours);
  const [minutes, setMinutes] = useState(initMinutes);
  const [isAM, setIsAM] = useState(initHours < 12);
  const [mode, setMode] = useState("hour"); // "hour" → select hour first, then minutes

  const displayHour = () => {
    const h = hours % 12;
    return h === 0 ? 12 : h;
  };

  const numbers =
    mode === "hour"
      ? Array.from({ length: 12 }, (_, i) => i + 1)
      : Array.from({ length: 12 }, (_, i) => i * 5);

  const handleSelect = (val) => {
    if (mode === "hour") {
      setHours(isAM ? val % 12 : (val % 12) + 12);
      setMode("minute");
    } else {
      setMinutes(val);
    }
  };

  const handAngle =
    mode === "hour"
      ? (displayHour() % 12) * 30
      : minutes * 6;

  const { t } = useTranslation();

  return (
    <div className="bg-white dark:bg-[#0a0a0a] rounded-2xl shadow-2xl w-full max-w-sm sm:w-80 z-50 overflow-hidden border border-gray-200 dark:border-[#2b2b2b] clock-popover">
      {/* Header */}
      <div className="bg-[#6d29f6] text-white p-6 text-center">
        <div className="text-sm opacity-90">{t("select_time")}</div>
        <div className="text-3xl font-bold">
          {String(displayHour()).padStart(2, "0")}:
          {String(minutes).padStart(2, "0")} {isAM ? t("am") : t("pm")}
        </div>
      </div>

      {/* Clock Face */}
      <div className="p-8 flex justify-center items-center">
        <div className="relative w-64 h-64 rounded-full border border-gray-300 dark:border-[#2e2e2e] flex items-center justify-center">
          {/* Center dot */}
          <div className="w-3 h-3 bg-[#6d29f6] rounded-full absolute z-20" />
          {/* Clock hand */}
          <div
            className="absolute w-1 bg-[#6d29f6] origin-bottom z-10"
            style={{
              height: mode === "hour" ? "80px" : "100px",
              bottom: "50%",
              left: "50%",
              transform: `rotate(${handAngle}deg) translateX(-50%)`,
              transformOrigin: "bottom",
            }}
          />
          {/* Numbers positioned around circle */}
          {numbers.map((num, idx) => {
            const value = mode === "hour" ? (idx === 0 ? 12 : idx) : idx * 5;
            const angle = (idx / numbers.length) * 2 * Math.PI - Math.PI / 2;
            const x = 100 * Math.cos(angle);
            const y = 100 * Math.sin(angle);
            const isSelected =
              mode === "hour" ? displayHour() === value : minutes === value;

            return (
              <button
                type="button"
                key={value}
                onClick={() => handleSelect(value)}
                className={`absolute w-10 h-10 rounded-full flex items-center justify-center transition-all duration-150 ${
                  isSelected
                    ? "bg-[#6d29f6] text-white"
                    : "bg-gray-100 dark:bg-[#2f3050] text-gray-800 dark:text-gray-100 hover:bg-gray-200 dark:hover:bg-[#3a3b5a]"
                }`}
                style={{
                  transform: `translate(${x}px, ${y}px)`,
                }}
              >
                {String(value).padStart(2, "0")}
              </button>
            );
          })}
        </div>
      </div>

      {/* AM/PM Toggle */}
      <div className="flex justify-center space-x-4 mt-2">
        <button
          type="button"
          onClick={() => setIsAM(true)}
          className={`px-4 py-2 rounded-full font-medium transition ${
            isAM
              ? "bg-[#6d29f6] text-white"
              : "bg-gray-100 dark:bg-[#2f3050] text-gray-800 dark:text-gray-100 hover:bg-gray-200 dark:hover:bg-[#3a3b5a]"
          }`}
        >
          {t("am")}
        </button>
        <button
          type="button"
          onClick={() => setIsAM(false)}
          className={`px-4 py-2 rounded-full font-medium transition ${
            !isAM
              ? "bg-[#6d29f6] text-white"
              : "bg-gray-100 dark:bg-[#2f3050] text-gray-800 dark:text-gray-100 hover:bg-gray-200 dark:hover:bg-[#3a3b5a]"
          }`}
        >
          {t("pm")}
        </button>
      </div>

      {/* Footer */}
      <div className="flex justify-between px-6 py-4 border-t border-gray-200 dark:border-[#2e2e2e] bg-gray-50 dark:bg-[#252739]">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 rounded-lg font-medium text-gray-700 dark:text-[#ff6b6b] bg-gray-200 dark:bg-[#1f1f1f] hover:bg-gray-300 dark:hover:bg-[#6d29f6] dark:hover:text-white transition"
        >
          {t("cancel")}
        </button>
        <button
          type="button"
          onClick={() => {
            let finalHours = hours % 12;
            onConfirm({ hours: finalHours, minutes, isAm: isAM });
          }}
          className="px-4 py-2 rounded-lg font-medium text-white bg-[#6d29f6] hover:bg-[#5b22d1] transition"
        >
          {t("ok")}
        </button>
      </div>
    </div>
  );
}


// --------------------------------------
// CreatePoll Component
// --------------------------------------
const CreatePoll = () => {
  const [pollData, setPollData] = useState({
    poll_title: "",
    options: ["", ""],
    expiry_date: "",
    expiry_time: "",
  });
  const [multiple, setMultiple] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [showCalendar, setShowCalendar] = useState(false);
  const [showClock, setShowClock] = useState(false);

  const [calendarSeed, setCalendarSeed] = useState(new Date());
  const [clockSeed, setClockSeed] = useState({ hours: 12, minutes: 0 });
  const { t } = useTranslation();

  useEffect(() => {
    if (showCalendar) {
      if (pollData.expiry_date) {
        const [y, m, d] = pollData.expiry_date.split("-").map(Number);
        setCalendarSeed(new Date(y, m - 1, d, 0, 0, 0));
      } else {
        setCalendarSeed(new Date());
      }
    }
    if (showClock) {
      if (pollData.expiry_time) {
        const timeMatch = pollData.expiry_time.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);
        if (timeMatch) {
          let hh = parseInt(timeMatch[1]);
          const mm = parseInt(timeMatch[2]);
          const ampm = timeMatch[3].toUpperCase();
          // Convert to 24-hour format for the clock seed
          if (ampm === "PM" && hh !== 12) {
            hh += 12;
          } else if (ampm === "AM" && hh === 12) {
            hh = 0;
          }
          setClockSeed({ hours: hh, minutes: mm });
        }
      } else {
        const now = new Date();
        setClockSeed({ hours: now.getHours(), minutes: now.getMinutes() });
      }
    }
  }, [showCalendar, showClock, pollData.expiry_date, pollData.expiry_time]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name === "multiple") setMultiple(checked);
    setPollData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleOptionChange = (index, value) => {
    const updatedOptions = [...pollData.options];
    updatedOptions[index] = value;
    setPollData((prev) => ({
      ...prev,
      options: updatedOptions,
    }));
  };

  const addOption = () => {
    if (pollData.options.length < 4) {
      setPollData((prev) => ({
        ...prev,
        options: [...prev.options, ""],
      }));
    }
  };

  const removeOption = (index) => {
    setPollData((prev) => ({
      ...prev,
      options: prev.options.filter((_, i) => i !== index),
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setSuccess("");

    if (!pollData.poll_title.trim()) {
      setError(t("poll_title_required"));
      toast.error(t("poll_title_required"));
      setLoading(false);
      return;
    }
    if (!pollData.expiry_date || !pollData.expiry_time) {
      setError(t("expiry_date_time_required"));
      toast.error(t("expiry_date_time_required"));
      setLoading(false);
      return;
    }
    if (
      !pollData.options.length ||
      pollData.options.some((opt) => !opt.trim())
    ) {
      setError(t("all_options_required"));
      toast.error(t("all_options_required"));
      setLoading(false);
      return;
    }

    // Parse the date from YYYY-MM-DD format
    const [year, month, day] = pollData.expiry_date.split("-").map(Number);
    // Parse the time from HH:MM AM/PM format
    const timeMatch = pollData.expiry_time.match(
      /(\d{1,2}):(\d{2})\s*(AM|PM)/i
    );
    if (!timeMatch) {
      setError(t("invalid_time_format"));
      toast.error(t("invalid_time_format"));
      setLoading(false);
      return;
    }

    let hours = parseInt(timeMatch[1]);
    const minutes = parseInt(timeMatch[2]);
    const ampm = timeMatch[3].toUpperCase();

    // Convert to 24-hour format
    if (ampm === "PM" && hours !== 12) {
      hours += 12;
    } else if (ampm === "AM" && hours === 12) {
      hours = 0;
    }

    // Create date object in local time
    const localDateTime = new Date(year, month - 1, day, hours, minutes, 0);

    // Convert to UTC
    const utcYear = localDateTime.getUTCFullYear();
    const utcMonth = String(localDateTime.getUTCMonth() + 1).padStart(2, "0");
    const utcDay = String(localDateTime.getUTCDate()).padStart(2, "0");
    const utcHour = String(localDateTime.getUTCHours()).padStart(2, "0");
    const utcMinute = String(localDateTime.getUTCMinutes()).padStart(2, "0");
    const expiry_datetime = `${utcYear}-${utcMonth}-${utcDay} ${utcHour}:${utcMinute}:00`;

    const selection = multiple ? "2" : "1";
    const payload = {
      poll_title: pollData.poll_title,
      selection,
      expiry_datetime,
      options: JSON.stringify(pollData.options),
    };

    createPoll(payload).then(() => {
      toast.success(t("poll_created_successfully"));
      setSuccess(t("poll_created_successfully"));
      setPollData({
        poll_title: "",
        options: ["", ""],
        expiry_date: "",
        expiry_time: "",
      });
    });
  };

  return (
    <CustomPageHeader
      // showPageHeaderBackButton={false}
      pageContainerInnerClass={""}
      // showPageHeader={false}
      pageContainerClass="px-2 "
      title={t("create_poll", "Create Your Vibe")}
    >
      <div className="w-full max-w-lg mx-auto">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-800">
            {t("create_poll")}
          </h1>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6 mt-4">
       
          {success && <div className="text-green-600 text-sm">{success}</div>}

          {/* Poll Title */}
          <div>
            <label className="block mb-1 text-gray-800">
              {t("poll_question")}
            </label>
            <input
              type="text"
              name="poll_title"
              value={pollData.poll_title}
              onChange={handleInputChange}
              placeholder={t("poll_question_placeholder")}
              className="w-full p-4 bg-gray-100 rounded-xl border shadow-sm border-gray-300 text-gray-800"
               required
            />
          </div>

          {/* Multiple Choice */}
          {/* <div className="flex items-center space-x-3">
            <input
              type="checkbox"
              name="multiple"
              checked={multiple}
              onChange={handleInputChange}
              className="w-5 h-5 accent-[#6d29f6] border-gray-300 rounded focus:ring-[#6d29f6] focus:ring-offset-gray-100"
            />
            <label className="text-gray-800">
              {t("allow_multiple_choices")}
            </label>
          </div> */}



<label className="relative flex items-center cursor-pointer">
                  <input
                    type="checkbox"
              name="multiple"
              checked={multiple}
              onChange={handleInputChange}
                    className="peer sr-only"
                  />

                  <div
                    className={`h-5 w-5 border-2 rounded-md flex items-center justify-center 
                  transition-all duration-200 ${
                    multiple
                      ? "bg-[#6d29f6] border-[#6d29f6]"
                      : "bg-transparent border-[#6d29f6]"
                  }`}
                  >
                    {multiple && (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 20 20"
                        fill="white"
                        className="w-3 h-3"
                      >
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-7.414 7.414a1 1 0 01-1.414 0L3.293 9.414a1 1 0 011.414-1.414L8.5 11.793l6.793-6.793a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                    )}
                  </div>
                   <label className="text-gray-800 ml-2 mt-0.5">
              {t("allow_multiple_choices")}
            </label>
                </label>


          {/* Expiry Date & Time */}
          <div>
            <label className="block mb-1 text-gray-800">
              {t("expiry_date_time")}
            </label>

            <div className="flex flex-col sm:flex-row gap-2">
              {/* Date */}
              <div className="relative w-full sm:w-1/2">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                    />
                  </svg>
                </span>
                <input
                  type="text"
                  name="expiry_date"
                  value={pollData.expiry_date || ""}
                  readOnly
                  onClick={() => {
                    setShowCalendar(!showCalendar);
                    setShowClock(false);
                  }}
                  className="w-full pl-10 p-4 bg-gray-100 rounded-lg text-gray-800 cursor-pointer placeholder:text-xs"
                  placeholder={t("expiry_date_placeholder")}
                />

                {/* Calendar Modal */}
                {showCalendar && (
                  <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm z-50">
                    <CalendarPopover
                      initialDate={calendarSeed}
                      onCancel={() => setShowCalendar(false)}
                      onConfirm={(date) => {
                        const y = date.getFullYear();
                        const m = zero(date.getMonth() + 1);
                        const d = zero(date.getDate());
                        setPollData((prev) => ({
                          ...prev,
                          expiry_date: `${y}-${m}-${d}`, // Store in YYYY-MM-DD format
                        }));
                        setShowCalendar(false);
                      }}
                    />
                  </div>
                )}
              </div>

              {/* Time */}
              <div className="relative w-full sm:w-1/2">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8v4l3 3"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </span>
                <input
                  type="text"
                  name="expiry_time"
                  value={pollData.expiry_time || ""}
                  readOnly
                  onClick={() => {
                    setShowClock(!showClock);
                    setShowCalendar(false);
                  }}
                  className="w-full pl-10 p-4 bg-gray-100 rounded-lg text-gray-800 cursor-pointer placeholder:text-xs"
                  placeholder={t("expiry_time_placeholder")}
                />

                {showClock && (
                  <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm z-50">
                    <ClockPopover
                      initialTime={clockSeed}
                      onCancel={() => setShowClock(false)}
                      onConfirm={(time) => {
                        setPollData((prev) => ({
                          ...prev,
                          expiry_time: `${zero(time.hours)}:${zero(
                            time.minutes
                          )} ${time.isAm ? "AM" : "PM"}`,
                        }));
                        setShowClock(false);
                      }}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Options */}
          <div>
            <label className="block mb-1 text-gray-800">{t("options")}</label>
            <div className="space-y-3">
              {pollData.options.map((opt, index) => (
                <div key={index} className="flex items-center space-x-2">
                  <input
                    type="text"
                    value={opt}
                    onChange={(e) => handleOptionChange(index, e.target.value)}
                    placeholder={t("option_placeholder", { number: index + 1 })}
                    className="flex-1 p-4 bg-gray-100 rounded-lg border border-gray-300 text-gray-800"
                    required
                  />
                  {pollData.options.length > 2 && (
                    <button
                      type="button"
                      onClick={() => removeOption(index)}
                      className="bg-red-100 text-red-600 p-3 rounded-lg hover:bg-red-200 transition"
                    >
                      ✕
                    </button>
                  )}
                </div>
              ))}
            </div>
            {pollData.options.length < 4 && (
              <button
                type="button"
                onClick={addOption}
                className="text-[#6d29f6] "
              >
                +{t("add_option")}
              </button>
            )}
          </div>

          {/* Submit */}
          <div className="pt-4">
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-[#6d29f6] text-white font-semibold py-4 rounded-lg hover:bg-[#5b22d1] transition"
            >
              {t("create_poll")}
            </button>
          </div>
        </form>
      </div>
    </CustomPageHeader>
  );
};

export default CreatePoll;
